import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { repo, isMockMode } from '../../services/repository'
import { useAuthStore } from '../../store/authStore'
import type { BillingInterval, Subscription } from '../../types/auth'
import { formatDate } from '../../utils/formatters'

export function BillingPage() {
  const { t } = useTranslation()
  const { user, refreshMe, isAuthenticated, isLoading } = useAuthStore()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState<'month' | 'year' | 'portal' | null>(null)

  useEffect(() => {
    if (isAuthenticated) {
      void refreshMe().catch(() => {
        /* ignore */
      })
    }
  }, [searchParams, refreshMe, isAuthenticated])

  async function startCheckout(interval: BillingInterval) {
    setError('')
    setLoading(interval)
    try {
      const { url } = await repo.subscriptions.createCheckout(interval)
      if (isMockMode) {
        await refreshMe()
        window.history.replaceState({}, '', '/billing?mock_upgraded=1')
      } else {
        window.location.href = url
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('billing.checkoutFailed', '开通失败'))
    } finally {
      setLoading(null)
    }
  }

  async function openPortal() {
    setError('')
    setLoading('portal')
    try {
      const { url } = await repo.subscriptions.createPortal()
      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : t('billing.portalFailed', '打开管理页失败'))
      setLoading(null)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">
        {t('common.loading', '加载中…')}
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('billing.needLogin', '请先登录后再管理订阅')}</p>
          <Link to="/login" className="text-violet-600 hover:underline">
            {t('auth.login', '登录')}
          </Link>
        </div>
      </div>
    )
  }

  const plan = user?.plan ?? 'free'
  const sub: Subscription = user?.subscription ?? {
    plan: 'free',
    billingInterval: null,
    status: 'active',
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
  }
  const isProActive = plan === 'pro'
  const endText = sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : null
  const success = searchParams.get('success') === '1' || searchParams.get('mock_upgraded') === '1'
  const canceled = searchParams.get('canceled') === '1'

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="max-w-lg mx-auto px-4 py-6">
        <Link to="/" className="text-sm text-violet-600 hover:underline">
          ← {t('billing.backGame', '返回游戏')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-3 mb-2">
          {t('billing.title', '订阅 Pro')}
        </h1>
        <p className="text-sm text-gray-500 mb-4">
          {t('billing.tagline', '云端同步 · 钻石奖励 · 优先体验新功能')}
        </p>

        {success && (
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 text-green-900 text-sm px-3 py-2.5">
            {t('billing.successHint', '支付成功！若状态未更新，请稍候刷新（以 Webhook 为准）')}
          </div>
        )}
        {canceled && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-950 text-sm px-3 py-2.5">
            {t('billing.canceledHint', '已取消结账，方案未变更')}
          </div>
        )}

        <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">{t('billing.currentPlan', '当前方案')}</span>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${
                isProActive ? 'bg-violet-100 text-violet-900' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {isProActive
                ? sub.cancelAtPeriodEnd
                  ? t('billing.proCanceled', 'Pro（已取消续费）')
                  : 'Pro'
                : 'Free'}
            </span>
            {isMockMode && <span className="text-xs text-gray-400">Mock</span>}
          </div>
          {isProActive && endText && (
            <p className="mt-2 text-sm text-gray-600">
              {sub.cancelAtPeriodEnd
                ? t('billing.endsAt', '权益至 {{date}}', { date: endText })
                : t('billing.renewsAt', '下次续费 {{date}}', { date: endText })}
            </p>
          )}
          {!isProActive && (
            <p className="mt-2 text-sm text-gray-600">
              {t('billing.freeDetail', '免费畅玩；升级 Pro 可获得订阅奖励钻石与完整云同步')}
            </p>
          )}
        </div>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        {!isProActive && (
          <div className="space-y-3">
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => startCheckout('month')}
              className="w-full rounded-xl bg-violet-600 text-white py-3 text-sm font-medium hover:bg-violet-700 disabled:opacity-60"
            >
              {loading === 'month'
                ? t('billing.processing', '处理中…')
                : t('billing.subscribeMonth', '按月订阅 Pro')}
            </button>
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => startCheckout('year')}
              className="w-full rounded-xl border border-violet-200 bg-white text-violet-700 py-3 text-sm font-medium hover:bg-violet-50 disabled:opacity-60"
            >
              {loading === 'year'
                ? t('billing.processing', '处理中…')
                : t('billing.subscribeYear', '按年订阅 Pro')}
            </button>
          </div>
        )}

        {isProActive && !isMockMode && (
          <button
            type="button"
            disabled={loading !== null}
            onClick={openPortal}
            className="w-full rounded-xl border border-gray-200 bg-white text-gray-800 py-3 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
          >
            {loading === 'portal'
              ? t('billing.processing', '处理中…')
              : t('billing.manage', '管理订阅（换卡 / 取消）')}
          </button>
        )}

        <ul className="mt-6 text-sm text-gray-600 space-y-2 list-disc pl-5">
          <li>{t('billing.benefit1', '登录后云端同步积分、钻石与道具')}</li>
          <li>{t('billing.benefit2', '开通 Pro 赠送 100 钻石（Webhook 到账）')}</li>
          <li>{t('billing.benefit3', '取消默认周期末生效，期内仍享 Pro')}</li>
        </ul>
      </div>
    </div>
  )
}
